/**
 * Sentry Error Tracking Service (Frontend)
 * Optional error tracking controlled by VITE_SENTRY_DSN environment variable
 * Implements PII scrubbing and React integration
 */

/**
 * Initialize Sentry for frontend error tracking
 * - Only initializes if VITE_SENTRY_DSN is set
 * - Scrubs PII from events (emails, tokens, auth headers)
 * - Integrates with React Router for navigation breadcrumbs
 * - Low performance sampling (5%) to minimize overhead
 * @returns {boolean} True if Sentry was initialized, false otherwise
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.debug('[Sentry] DSN not provided, skipping initialization');
    return false;
  }

  try {
    // Dynamic import to avoid loading Sentry if not needed
    import('@sentry/react').then((Sentry) => {
      Sentry.init({
        dsn,
        environment: import.meta.env.VITE_ENV || import.meta.env.MODE || 'dev',
        release: import.meta.env.VITE_GIT_SHA || 'dev',
        integrations: [
          new Sentry.BrowserTracing({
            // Optional: React Router integration for navigation tracking
            // routingInstrumentation: Sentry.reactRouterV6Instrumentation(...)
          })
        ],
        tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.05'),
        sendDefaultPii: false, // Never send PII by default

        beforeSend(event, hint) {
          // Scrub sensitive headers
          if (event.request && event.request.headers) {
            delete event.request.headers['Authorization'];
            delete event.request.headers['authorization'];
            delete event.request.headers['Cookie'];
            delete event.request.headers['cookie'];
          }

          // Scrub sensitive request data
          if (event.request && event.request.data) {
            const data = event.request.data;
            // Remove potential tokens, passwords, card data
            const sensitiveKeys = [
              'token',
              'password',
              'card',
              'cardNumber',
              'cvc',
              'cvv',
              'apiKey',
              'secret',
              'accessToken',
              'refreshToken'
            ];

            if (data && typeof data === 'object') {
              sensitiveKeys.forEach((key) => {
                if (key in data) {
                  data[key] = '[REDACTED]';
                }
              });
            }
          }

          // Do not capture participant names or emails in breadcrumbs
          if (event.breadcrumbs) {
            event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
              if (breadcrumb.data) {
                const data = { ...breadcrumb.data };
                ['email', 'name', 'displayName', 'participantName'].forEach((key) => {
                  if (key in data) {
                    data[key] = '[REDACTED]';
                  }
                });
                return { ...breadcrumb, data };
              }
              return breadcrumb;
            });
          }

          return event;
        }
      });

      console.info('[Sentry] Initialized', {
        environment: import.meta.env.VITE_ENV || import.meta.env.MODE || 'dev',
        tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.05')
      });
    });

    return true;
  } catch (err) {
    console.warn('[Sentry] Initialization failed', err.message);
    return false;
  }
}

/**
 * Set user context for Sentry (only hashed UID, no email or name)
 * @param {Object} user - User object with uid
 */
export function setUserContext(user) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || !user) return;

  import('@sentry/react').then((Sentry) => {
    // Only set hashed UID for correlation, never PII
    Sentry.setUser({
      id: user.uid || null
      // Do NOT include: email, name, displayName, or any PII
    });
  });
}

/**
 * Clear user context from Sentry
 */
export function clearUserContext() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  import('@sentry/react').then((Sentry) => {
    Sentry.setUser(null);
  });
}

/**
 * Manually capture an exception
 * @param {Error} error - Error to capture
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  import('@sentry/react').then((Sentry) => {
    Sentry.captureException(error, { extra: context });
  });
}
